import type { ReactElement, ReactNode } from "react";

import { cn } from "@/lib/utils";

function isBulletLine(line: string): boolean {
  return line.startsWith("- ");
}

function isNumberedLine(line: string): boolean {
  return /^\d+\.\s/.test(line);
}

/** Turns `**label**` into bold so help markdown is not shown as raw asterisks. */
function renderHelpInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  if (parts.length === 1) return text;

  return parts.map((part, index) => {
    const bold = /^\*\*([^*]+)\*\*$/.exec(part);
    if (bold?.[1]) {
      return <strong key={index}>{bold[1]}</strong>;
    }
    return part;
  });
}

export function HelpFaqAnswer({
  text,
  className,
}: {
  text: string;
  className?: string;
}): ReactElement {
  const blocks = text.split(/\n\n+/);

  return (
    <div className={cn("space-y-3 text-sm", className)}>
      {blocks.map((block, blockIndex) => {
        const lines = block.split("\n").filter((line) => line.length > 0);
        if (lines.length > 0 && lines.every(isBulletLine)) {
          return (
            <ul key={blockIndex} className="list-disc space-y-1 pl-4">
              {lines.map((line) => (
                <li key={line}>{renderHelpInline(line.slice(2))}</li>
              ))}
            </ul>
          );
        }
        if (lines.length > 0 && lines.every(isNumberedLine)) {
          return (
            <ol key={blockIndex} className="list-decimal space-y-1 pl-4">
              {lines.map((line) => (
                <li key={line}>{renderHelpInline(line.replace(/^\d+\.\s/, ""))}</li>
              ))}
            </ol>
          );
        }
        return (
          <p key={blockIndex} className="whitespace-pre-line">
            {renderHelpInline(block)}
          </p>
        );
      })}
    </div>
  );
}
